import { memo, useCallback, useMemo } from "react";

import { VStack } from "@/components/Layout";

import { useDynamicCollections } from "../../hooks/useForm/useDynamicCollections";
import { useFormStoreContext } from "../../store/zustand-context";
import type { AttributeCollectionId } from "../../types";
import { AttributeCollection } from "../collections/AttributeCollection";
import { CollectionToggle } from "../collections/CollectionToggle";
import { DynamicCollectionAddButton } from "../collections/DynamicCollectionAddButton";
import { RemoveButton } from "../collections/RemoveButton";

interface DynamicCollectionsContainerProps {
  /** コレクションID */
  collectionId: AttributeCollectionId;
  /** 読み取り専用モード */
  readOnly?: boolean;
  /** レイアウト */
  layout?: "vertical" | "horizontal";
  /** クラス名 */
  className?: string;
  /** テストID */
  "data-testid"?: string;
}

/**
 * 動的コレクションコンテナ
 * 同じコレクションIDを持つ複数のコレクションインスタンスを管理し、表示するコンポーネント
 *
 * このコンポーネントは、FormRendererが動的コレクションを表示するために使用します。
 * 通常は直接使用せず、FormRendererを介して使用します。
 */
export const DynamicCollectionsContainer = memo(
  ({
    collectionId,
    readOnly,
    layout = "vertical",
    className,
    "data-testid": testId = "dynamic-collections-container",
  }: DynamicCollectionsContainerProps) => {
    const store = useFormStoreContext();
    const {
      collectionIndices,
      canAddCollection,
      handleAddCollection,
      handleRemoveCollection,
      toggleCollectionEnabled,
    } = useDynamicCollections(collectionId);

    // コレクションが任意入力かどうかをチェック
    const isOptional = store.isOptionalCollection?.(collectionId) ?? false;

    // コレクションの入力スキップ状態を切り替えるハンドラー
    const handleToggleCollection = useCallback(
      (index: number, enabled: boolean) => {
        toggleCollectionEnabled(index, enabled);
      },
      [toggleCollectionEnabled],
    );

    // コレクションが有効かどうかを取得
    const isCollectionEnabled = useCallback(
      (index: number) => {
        return store.isCollectionEnabled?.(collectionId, index) ?? true;
      },
      [store, collectionId],
    );

    // 少なくとも1つのコレクションが有効かどうかを確認
    const hasAnyEnabledCollection = useMemo(() => {
      if (!isOptional) {
        // 任意入力でない場合は常に有効とみなす
        return true;
      }

      if (collectionIndices.length === 0) {
        // コレクションがない場合は有効とみなす（追加ボタンは表示しない条件で処理）
        return true;
      }

      // いずれかのコレクションが有効かチェック
      return collectionIndices.some((index) => isCollectionEnabled(index));
    }, [isOptional, collectionIndices, isCollectionEnabled]);

    // 追加ボタン表示条件
    // 1. 追加可能な状態であること
    // 2. 読取専用モードでないこと
    // 3. 既にコレクションが存在すること
    // 4. 少なくとも1つのコレクションが有効であること
    const shouldShowAddButton =
      canAddCollection &&
      !readOnly &&
      collectionIndices.length > 0 &&
      hasAnyEnabledCollection;

    return (
      <div
        className={`space-y-6 ${className || ""}`}
        data-testid={testId}
        data-collection-id={collectionId}
      >
        {/* 動的コレクションインスタンスの表示 */}
        {collectionIndices.map((index) => (
          <VStack
            key={`dynamic-collection-${collectionId}-${index}`}
            className="mb-4 relative"
          >
            {/* オプショナルコレクションの場合にトグルを表示（readOnlyでは表示しない） */}
            {isOptional && !readOnly && collectionIndices.length === 1 && (
              <CollectionToggle
                collectionId={collectionId}
                collectionIndex={index}
                enabled={isCollectionEnabled(index)}
                onToggle={(enabled) => handleToggleCollection(index, enabled)}
              />
            )}

            {/* 削除ボタン - 複数コレクションが存在し、ReadOnlyでない場合に表示 */}
            {!readOnly && collectionIndices.length > 1 && (
              <div className="absolute right-0 top-0 z-10">
                <RemoveButton
                  index={index}
                  onRemove={() => handleRemoveCollection(index)}
                  type="collection"
                />
              </div>
            )}

            {/* コンテンツ表示
                readOnlyモードの場合は常に表示
                readOnlyでない場合は、オプショナルでないか有効な場合に表示 */}
            {(readOnly || !isOptional || isCollectionEnabled(index)) && (
              <AttributeCollection
                collectionId={collectionId}
                collectionIndex={index}
                readOnly={readOnly}
                layout={layout}
                data-testid={`collection-${collectionId}-${index}`}
              />
            )}
          </VStack>
        ))}

        {/* 動的コレクション追加ボタン */}
        {shouldShowAddButton && (
          <DynamicCollectionAddButton
            onClick={handleAddCollection}
            data-testid="add-dynamic-collection"
          />
        )}
      </div>
    );
  },
);

DynamicCollectionsContainer.displayName = "DynamicCollectionsContainer";
